import { stringWidth } from '../unicode/width';

/**
 * Text layout and large-text rendering.
 *
 * A terminal document has no fonts — the terminal picks the font — so "text
 * styles" here mean two honest things: wrapping/alignment inside a box, and
 * drawing oversized letterforms out of characters (block text or FIGlet).
 */

export type TextAlign = 'left' | 'center' | 'right';

export interface TextLayoutOptions {
  width: number;
  height: number;
  align: TextAlign;
  wrap: boolean;
  /** Drop lines that fall outside the box instead of overflowing. */
  clip: boolean;
}

export interface LaidOutLine {
  text: string;
  x: number;
  y: number;
}

/** Word-wraps, breaking over-long words, and measures in terminal columns. */
export function wrapText(text: string, width: number): string[] {
  if (width <= 0) return [text];
  const out: string[] = [];
  for (const paragraph of text.split('\n')) {
    if (paragraph === '') {
      out.push('');
      continue;
    }
    let line = '';
    for (const word of paragraph.split(' ')) {
      const candidate = line === '' ? word : `${line} ${word}`;
      if (stringWidth(candidate) <= width) {
        line = candidate;
        continue;
      }
      if (line !== '') out.push(line);
      if (stringWidth(word) <= width) {
        line = word;
        continue;
      }
      // Break a word that cannot fit on any line.
      let chunk = '';
      for (const ch of word) {
        if (stringWidth(chunk + ch) > width) {
          out.push(chunk);
          chunk = ch;
        } else {
          chunk += ch;
        }
      }
      line = chunk;
    }
    out.push(line);
  }
  return out;
}

export function layoutText(text: string, opts: TextLayoutOptions): LaidOutLine[] {
  const lines = opts.wrap ? wrapText(text, opts.width) : text.split('\n');
  const visible = opts.clip ? lines.slice(0, Math.max(0, opts.height)) : lines;
  return visible.map((line, i) => {
    const w = stringWidth(line);
    let x = 0;
    if (opts.align === 'center') x = Math.max(0, Math.floor((opts.width - w) / 2));
    else if (opts.align === 'right') x = Math.max(0, opts.width - w);
    const clipped = opts.clip && w > opts.width ? sliceToWidth(line, opts.width) : line;
    return { text: clipped, x, y: i };
  });
}

export function sliceToWidth(text: string, width: number): string {
  let out = '';
  let w = 0;
  for (const ch of text) {
    const cw = stringWidth(ch);
    if (w + cw > width) break;
    out += ch;
    w += cw;
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * Block text: a 5x5 bitmap font rendered with terminal characters
 * ------------------------------------------------------------------ */

/**
 * Each glyph is 5 rows of 5 bits, most significant bit leftmost.
 * Authoring this as bit rows keeps it compact and, unlike ASCII art, makes
 * every render style (full block, half block, outline) fall out of one source.
 */
const FONT_5X5: Record<string, number[]> = {
  A: [0b01110, 0b10001, 0b11111, 0b10001, 0b10001],
  B: [0b11110, 0b10001, 0b11110, 0b10001, 0b11110],
  C: [0b01111, 0b10000, 0b10000, 0b10000, 0b01111],
  D: [0b11110, 0b10001, 0b10001, 0b10001, 0b11110],
  E: [0b11111, 0b10000, 0b11110, 0b10000, 0b11111],
  F: [0b11111, 0b10000, 0b11110, 0b10000, 0b10000],
  G: [0b01111, 0b10000, 0b10011, 0b10001, 0b01111],
  H: [0b10001, 0b10001, 0b11111, 0b10001, 0b10001],
  I: [0b11111, 0b00100, 0b00100, 0b00100, 0b11111],
  J: [0b00111, 0b00010, 0b00010, 0b10010, 0b01100],
  K: [0b10001, 0b10010, 0b11100, 0b10010, 0b10001],
  L: [0b10000, 0b10000, 0b10000, 0b10000, 0b11111],
  M: [0b10001, 0b11011, 0b10101, 0b10001, 0b10001],
  N: [0b10001, 0b11001, 0b10101, 0b10011, 0b10001],
  O: [0b01110, 0b10001, 0b10001, 0b10001, 0b01110],
  P: [0b11110, 0b10001, 0b11110, 0b10000, 0b10000],
  Q: [0b01110, 0b10001, 0b10101, 0b10010, 0b01101],
  R: [0b11110, 0b10001, 0b11110, 0b10010, 0b10001],
  S: [0b01111, 0b10000, 0b01110, 0b00001, 0b11110],
  T: [0b11111, 0b00100, 0b00100, 0b00100, 0b00100],
  U: [0b10001, 0b10001, 0b10001, 0b10001, 0b01110],
  V: [0b10001, 0b10001, 0b10001, 0b01010, 0b00100],
  W: [0b10001, 0b10001, 0b10101, 0b11011, 0b10001],
  X: [0b10001, 0b01010, 0b00100, 0b01010, 0b10001],
  Y: [0b10001, 0b01010, 0b00100, 0b00100, 0b00100],
  Z: [0b11111, 0b00010, 0b00100, 0b01000, 0b11111],
  '0': [0b01110, 0b10011, 0b10101, 0b11001, 0b01110],
  '1': [0b00100, 0b01100, 0b00100, 0b00100, 0b01110],
  '2': [0b01110, 0b10001, 0b00110, 0b01000, 0b11111],
  '3': [0b11110, 0b00001, 0b01110, 0b00001, 0b11110],
  '4': [0b00010, 0b00110, 0b01010, 0b11111, 0b00010],
  '5': [0b11111, 0b10000, 0b11110, 0b00001, 0b11110],
  '6': [0b01110, 0b10000, 0b11110, 0b10001, 0b01110],
  '7': [0b11111, 0b00001, 0b00010, 0b00100, 0b00100],
  '8': [0b01110, 0b10001, 0b01110, 0b10001, 0b01110],
  '9': [0b01110, 0b10001, 0b01111, 0b00001, 0b01110],
  ' ': [0, 0, 0, 0, 0],
  '.': [0, 0, 0, 0b00100, 0b00100],
  ',': [0, 0, 0, 0b00100, 0b01000],
  '!': [0b00100, 0b00100, 0b00100, 0b00000, 0b00100],
  '?': [0b01110, 0b10001, 0b00110, 0b00000, 0b00100],
  ':': [0, 0b00100, 0, 0b00100, 0],
  '-': [0, 0, 0b11111, 0, 0],
  '_': [0, 0, 0, 0, 0b11111],
  '+': [0, 0b00100, 0b01110, 0b00100, 0],
  '=': [0, 0b11111, 0, 0b11111, 0],
  '/': [0b00001, 0b00010, 0b00100, 0b01000, 0b10000],
  '\\': [0b10000, 0b01000, 0b00100, 0b00010, 0b00001],
  '*': [0b10101, 0b01110, 0b11111, 0b01110, 0b10101],
  '#': [0b01010, 0b11111, 0b01010, 0b11111, 0b01010],
  '<': [0b00010, 0b00100, 0b01000, 0b00100, 0b00010],
  '>': [0b01000, 0b00100, 0b00010, 0b00100, 0b01000],
  '(': [0b00010, 0b00100, 0b00100, 0b00100, 0b00010],
  ')': [0b01000, 0b00100, 0b00100, 0b00100, 0b01000],
  '[': [0b00110, 0b00100, 0b00100, 0b00100, 0b00110],
  ']': [0b01100, 0b00100, 0b00100, 0b00100, 0b01100],
  "'": [0b00100, 0b00100, 0, 0, 0],
  '"': [0b01010, 0b01010, 0, 0, 0],
  '%': [0b10001, 0b00010, 0b00100, 0b01000, 0b10001],
  '@': [0b01110, 0b10011, 0b10111, 0b10000, 0b01110],
  '&': [0b01100, 0b10000, 0b01101, 0b10010, 0b01101],
};

export type BlockFontStyle = 'full' | 'half' | 'shadow' | 'outline' | 'small';

export const BLOCK_FONT_STYLES: readonly BlockFontStyle[] = ['full', 'half', 'shadow', 'outline', 'small'];

const GLYPH_WIDTH = 5;
const GLYPH_HEIGHT = 5;

function glyphRows(ch: string): number[] {
  const key = ch.toUpperCase();
  return FONT_5X5[key] ?? FONT_5X5[' '];
}

function bitsToRows(text: string, spacing: number): boolean[][] {
  const chars = [...text];
  const width = chars.length * (GLYPH_WIDTH + spacing) - (chars.length > 0 ? spacing : 0);
  const rows: boolean[][] = Array.from({ length: GLYPH_HEIGHT }, () =>
    new Array<boolean>(Math.max(0, width)).fill(false),
  );
  chars.forEach((ch, i) => {
    const glyph = glyphRows(ch);
    const ox = i * (GLYPH_WIDTH + spacing);
    for (let r = 0; r < GLYPH_HEIGHT; r++) {
      for (let c = 0; c < GLYPH_WIDTH; c++) {
        if ((glyph[r] >> (GLYPH_WIDTH - 1 - c)) & 1) rows[r][ox + c] = true;
      }
    }
  });
  return rows;
}

/**
 * Renders text as large letterforms.
 *
 * `half` packs two bitmap rows into one terminal row with ▀ ▄ █, giving text
 * that is 3 rows tall instead of 5 but keeps the same shape — usually the best
 * trade-off in a 24-row terminal.
 */
export function renderBlockText(text: string, style: BlockFontStyle = 'full', spacing = 1): string[] {
  if (style === 'small') {
    return [text.toUpperCase()];
  }
  const rows = bitsToRows(text, spacing);
  const width = rows[0]?.length ?? 0;

  if (style === 'half') {
    const out: string[] = [];
    for (let r = 0; r < GLYPH_HEIGHT; r += 2) {
      let line = '';
      for (let c = 0; c < width; c++) {
        const top = rows[r]?.[c] ?? false;
        const bottom = rows[r + 1]?.[c] ?? false;
        line += top && bottom ? '█' : top ? '▀' : bottom ? '▄' : ' ';
      }
      out.push(line);
    }
    return out;
  }

  if (style === 'outline') {
    const out: string[] = [];
    for (let r = 0; r < GLYPH_HEIGHT; r++) {
      let line = '';
      for (let c = 0; c < width; c++) {
        if (!rows[r][c]) {
          line += ' ';
          continue;
        }
        const edge =
          !(rows[r - 1]?.[c] ?? false) ||
          !(rows[r + 1]?.[c] ?? false) ||
          !(rows[r][c - 1] ?? false) ||
          !(rows[r][c + 1] ?? false);
        line += edge ? '█' : '░';
      }
      out.push(line);
    }
    return out;
  }

  if (style === 'shadow') {
    const out: string[] = [];
    for (let r = 0; r < GLYPH_HEIGHT; r++) {
      let line = '';
      for (let c = 0; c < width + 1; c++) {
        const solid = rows[r]?.[c] ?? false;
        const shadow = rows[r - 1]?.[c - 1] ?? false;
        line += solid ? '█' : shadow ? '▒' : ' ';
      }
      out.push(line);
    }
    return out;
  }

  return rows.map((row) => row.map((on) => (on ? '█' : ' ')).join(''));
}

/* ------------------------------------------------------------------ *
 * FIGlet
 * ------------------------------------------------------------------ */

export interface FigletFont {
  name: string;
  height: number;
  baseline: number;
  maxLength: number;
  hardBlank: string;
  oldLayout: number;
  glyphs: Map<number, string[]>;
}

/**
 * Parses a FIGlet `.flf` font.
 *
 * Supports the standard header, the required ASCII range (32-126), the German
 * codepage additions, and the tagged code-point extension block — which is
 * everything real-world fonts use.
 */
export function parseFlf(source: string): FigletFont {
  const lines = source.split(/\r?\n/);
  const header = lines[0] ?? '';
  const m = /^flf2a(.)\s+(-?\d+)\s+(-?\d+)\s+(-?\d+)\s+(-?\d+)\s+(\d+)/.exec(header);
  if (!m) throw new Error('Not a FIGlet font: missing flf2a header');
  const hardBlank = m[1];
  const height = Number(m[2]);
  const baseline = Number(m[3]);
  const maxLength = Number(m[4]);
  const oldLayout = Number(m[5]);
  const commentLines = Number(m[6]);

  const glyphs = new Map<number, string[]>();
  let i = 1 + commentLines;

  const readGlyph = (): string[] | null => {
    const rows: string[] = [];
    for (let r = 0; r < height; r++) {
      const line = lines[i + r];
      if (line === undefined) return null;
      rows.push(line.replace(/[@#$%]+\s*$/, (tail) => (tail.includes('@') ? '' : tail)).replace(/@+$/, ''));
    }
    i += height;
    return rows;
  };

  for (let cp = 32; cp <= 126; cp++) {
    const g = readGlyph();
    if (!g) break;
    glyphs.set(cp, g);
  }
  for (const cp of [196, 214, 220, 228, 246, 252, 223]) {
    const g = readGlyph();
    if (!g) break;
    glyphs.set(cp, g);
  }
  while (i < lines.length) {
    const tag = lines[i]?.trim();
    if (!tag) break;
    const codeMatch = /^(-?\d+|0x[0-9a-fA-F]+)/.exec(tag);
    if (!codeMatch) break;
    i += 1;
    const cp = codeMatch[1].startsWith('0x')
      ? Number.parseInt(codeMatch[1], 16)
      : Number.parseInt(codeMatch[1], 10);
    const g = readGlyph();
    if (!g) break;
    glyphs.set(cp, g);
  }

  const name = /^flf2a.\s.*$/.test(header) ? 'FIGlet font' : 'FIGlet font';
  return { name, height, baseline, maxLength, hardBlank, oldLayout, glyphs };
}

/** Renders text with a parsed FIGlet font (full-width layout, no kerning). */
export function renderFiglet(font: FigletFont, text: string): string[] {
  const out = new Array<string>(font.height).fill('');
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 32;
    const glyph = font.glyphs.get(cp) ?? font.glyphs.get(32);
    if (!glyph) continue;
    for (let r = 0; r < font.height; r++) {
      out[r] += (glyph[r] ?? '').split(font.hardBlank).join(' ');
    }
  }
  return out;
}
